import React, { Suspense } from "react";
import cn from "classnames";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaWhatsapp } from "react-icons/fa";
import { Button, Modal } from "react-bootstrap";

import { Leg } from "services/action";

import { Page } from "ui/components";
import CardContainerDetail from "ui/components/card/card.container.detail";

import {
  waFormat,
  formatPhone,
  currencyFormat,
  formatDate,
  UTCFormat,
} from "utilities/common";

const MapBox = React.lazy(() => import("ui/components/maps/route"));

const LegScreen = ({ match, location }) => {
  const Dispatch = useDispatch();
  const History = useHistory();
  const Session = useSelector((state) => state?.Auth?.session);

  const [data, setData] = React.useState(null);
  const [show, setShow] = React.useState(false);
  const [jobs, setJobs] = React.useState([]);
  const [totalCOD, setTCOD] = React.useState(0);

  const $onLoad = async (manifestID, legID) => {
    const req = await Dispatch(Leg.$show(manifestID, legID));

    if (req?.message === "success") {
      setData(req?.data);
    }
  };

  const handleChecklist = (id) => {
    // Check if the item is already in the list of checked items
    if (jobs.includes(id)) {
      // If it's in the list, remove it
      setJobs(jobs.filter((job) => job !== id));
    } else {
      // If it's not in the list, add it
      setJobs([...jobs, id]);
    }
  };

  const nextStep = () => {
    History.push(`/pod/${data?.id}/loading`, {
      manifest_id: location?.state?.manifest_id,
      jobs: jobs,
    });
  };

  const ListHorizontal = ({ title, content, className }) => {
    const c = cn(
      className ? className : "",
      "row py-1 mb-1 list-item m-0 align-items-start fsz-12 border-bottom"
    );

    return (
      <div className={c}>
        <div className="col-4 p-0 fsz-12">{title}</div>
        <div className="col-1 p-0 fsz-12">:</div>
        <div className="col-7 p-0 text-start title fsz-12">{content}</div>
      </div>
    );
  };

  const Arrival = () => {
    const $handleSubmit = async () => {
      const payload = {
        actor_name: Session?.name,
        actor_phone: Session?.phone,
        ata_at: UTCFormat(
          formatDate(new Date(), "YYYY-MM-DD"),
          formatDate(new Date(), "HH:mm")
        ),
      };

      const req = await Dispatch(
        Leg.$arrival(location?.state?.manifest_id, data?.id, payload)
      );
      if (req?.message === "success") {
        $onLoad(location?.state?.manifest_id, data?.id);
      }
    };

    return (
      <div className="col">
        <Button
          type="button"
          variant="success"
          className="w-100 fs-7"
          onClick={$handleSubmit}
        >
          Sampai Ditujuan
        </Button>
      </div>
    );
  };

  const Departure = () => {
    const $handleSubmit = async () => {
      const payload = {
        actor_name: Session?.name,
        actor_phone: Session?.phone,
        atd_at: UTCFormat(
          formatDate(new Date(), "YYYY-MM-DD"),
          formatDate(new Date(), "HH:mm")
        ),
      };

      const req = await Dispatch(
        Leg.$depature(location?.state?.manifest_id, data?.id, payload)
      );
      if (req?.message === "success") {
        $onLoad(location?.state?.manifest_id, data?.id);
      }
    };

    return (
      <div className="col">
        <Button
          type="button"
          variant="success"
          className="w-100 fs-7"
          onClick={$handleSubmit}
        >
          Menuju tujuan
        </Button>
      </div>
    );
  };

  const Loading = () => {
    return (
      <div className="col">
        <Button
          type="button"
          variant="success"
          className="w-100 fs-7"
          onClick={() => {
            History.push(`/pod/${data?.id}/loading`, {
              manifest_id: location?.state?.manifest_id,
            });
          }}
        >
          Muat Barang
        </Button>
      </div>
    );
  };

  const ActionDispute = () => {
    return (
      <div className="col">
        <Button
          type="button"
          variant="danger"
          className="w-100 fs-7"
          onClick={() =>
            History.push(`/pod/${data?.id}`, {
              type: "disputed",
              manifest_id: location?.state?.manifest_id,
            })
          }
        >
          Gagal Kirim
        </Button>
      </div>
    );
  };

  const ActionDelivery = () => {
    return (
      <div className="col">
        <Button
          type="button"
          variant="success"
          className="w-100 fs-7"
          onClick={() =>
            History.push(`/pod/${data?.id}`, {
              type: "delivered",
              manifest_id: location?.state?.manifest_id,
              cod_value: totalCOD,
              manifest_type: location?.state?.manifest_type,
            })
          }
        >
          Terkirim
        </Button>
      </div>
    );
  };

  const ActionCollect = () => {
    return (
      <div className="col">
        <Button
          type="button"
          variant="success"
          className="w-100 fs-7"
          onClick={() =>
            History.push(`/collect/${data?.id}`, {
              manifest_id: location?.state?.manifest_id,
              cod_value: totalCOD,
              manifest_type: location?.state?.manifest_type,
            })
          }
        >
          Terima COD
        </Button>
      </div>
    );
  };

  React.useEffect(() => {
    if (match?.params?.id && location?.state?.manifest_id) {
      $onLoad(location?.state?.manifest_id, match?.params?.id);
    }
  }, [match, location]);

  React.useEffect(() => {
    const totalCODValue = data?.jobs?.reduce((total, job) => {
      const codValue = job?.delivery_order?.shipment?.cod_value || 0;
      return total + codValue;
    }, 0);

    setTCOD(totalCODValue);
  }, [data]);

  return (
    <Page
      title={`Detail Lokasi`}
      full
      backTo={() =>
        location?.state?.manifest_id
          ? History.push(`/manifest/${location?.state?.manifest_id}`)
          : false
      }
      action={
        <div>
          {data?.status === "new" && <Departure />}
          {data?.status === "departed" && <Arrival />}
          {data?.status === "arrived" &&
            data?.type === "pickup" &&
            data?.loading_at === "0001-01-01T00:00:00Z" && <Loading />}
          {data?.status === "arrived" && data?.type === "drop_point" && (
            <div className="row mb-2">
              {data?.is_receive ? (
                <ActionDelivery />
              ) : (
                <>
                  <ActionDelivery />
                  <ActionDispute />
                </>
              )}
            </div>
          )}
          {((data?.status === 'delivered' || data?.status === 'partial_delivered') && !data?.is_receive && data?.cod_value > 0 && data?.cod_status === 'pending') && (
            <ActionCollect />
          )}
        </div>
      }
    >
      <Suspense fallback={<div>Loading...</div>}>
        <MapBox destination={data?.location} centerDynamic={false} />
      </Suspense>

      <div className="p-2 pb-5">
        <div className="d-flex gap-2 flex-row align-items-center mb-2">
          <div>
            <span className={`badge type-${data?.type}-lt ms-auto my-1`}>
              {data?.type?.replace("_", " ")}
            </span>
          </div>
          <div>
            <span className={`badge status-${data?.status}-lt ms-auto my-1`}>
              {data?.status?.replace("_", " ")}
            </span>
          </div>
        </div>

        <CardContainerDetail title="Informasi Lokasi">
          <ListHorizontal
            title="Tujuan"
            content={
              <div>
                <div className="fw-bold fsz-12">{data?.location?.name}</div>
                {data?.location?.name === data?.location?.contact_person ? (
                  <div className="page-pretitle">
                    {waFormat(data?.location?.phone_number)}
                  </div>
                ) : (
                  <div className="page-pretitle">
                    {data?.location?.contact_person} -{" "}
                    {waFormat(data?.location?.phone_number)}
                  </div>
                )}

                {formatPhone(data?.location?.phone_number) ? (
                  <div
                    className="btn btn-block bg-success py-1 text-white fsz-12 border-0"
                    // style={{ backgroundColor: "#01E675" }}
                    onClick={() =>
                      window.open(
                        `https://wa.me/${waFormat(
                          data?.location?.phone_number
                        )}`,
                        "_blank"
                      )
                    }
                  >
                    <FaWhatsapp size="15" color="#fff" className="me-1" />{" "}
                    Kontak
                  </div>
                ) : null}
              </div>
            }
          />

          <ListHorizontal
            title="Alamat"
            content={
              <div className="">
                <div className="fw-bold fsz-12">{data?.location?.address}</div>
                <div className="fsz-12">
                  {`${data?.location?.region?.district_name}, ${data?.location?.region?.regency_name}, ${data?.location?.region?.province_name}`}
                </div>
              </div>
            }
          />

          {data?.disputed_note && (
            <ListHorizontal
              title="Dispute Note"
              content={
                <div style={{ fontWeight: "bold" }}>{data?.disputed_note}</div>
              }
            />
          )}
        </CardContainerDetail>

        {data?.type === "pickup" && (
          <CardContainerDetail title="Daftar Pickup">
            {data?.jobs && (
              <div>
                {data?.jobs?.map((d, i) => (
                  <div
                    key={i}
                    className="row border-bottom py-1 align-items-center"
                  >
                    <div className="col-4">
                      <div className="table-col">
                        <div className="text-ellipsis fs-8 fw-bold text-uppercase">
                          {d?.delivery_order?.shipment?.code}
                        </div>
                        <small className="info text-uppercase">
                          ORD: {d?.delivery_order?.order?.code}
                        </small>

                        {d?.delivery_order?.shipment?.ref_code && (
                          <small className="info text-uppercase">
                            REF: {d?.delivery_order?.shipment?.ref_code}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-6 fs-8 fw-semibold">
                      <div className="table-col">
                        <div className="text-ellipsis fs-8 fw-bold text-uppercase">
                          {d?.destination?.name}
                        </div>
                        <small className="info text-uppercase fw-semibold">
                          {d?.destination?.region?.district_name},{" "}
                          {d?.destination?.region?.regency_name},{" "}
                          {d?.destination?.region?.province_name}
                        </small>
                      </div>
                    </div>
                    <div className="col-2 fs-8">
                      <div className="table-col">
                        <div className="text-ellipsis fs-8 fw-bold text-uppercase">
                          {currencyFormat(
                            d?.delivery_order?.shipment?.total_weight
                          )}{" "}
                          KG
                        </div>
                        <small className="info text-uppercase">
                          {d?.delivery_order?.shipment?.total_quantity} Koli
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContainerDetail>
        )}

        {data?.type === "drop_point" &&
          data?.jobs &&
          data?.jobs?.map((d, i) => (
            <>
              {d?.status !== "unchecked" && (
                <CardContainerDetail title="Detail Shipment" key={i}>
                  <ListHorizontal
                    title="Kode"
                    content={
                      <div className="fw-bold">
                        {d?.delivery_order?.shipment?.code}
                      </div>
                    }
                  />
                  {d?.delivery_order?.shipment?.ref_code && (
                    <ListHorizontal
                      title="Kode Referensi"
                      content={
                        <div className="fw-bold">
                          {d?.delivery_order?.shipment?.ref_code}
                        </div>
                      }
                    />
                  )}

                  <ListHorizontal
                    title="Shipper"
                    content={
                      <div className="fw-bold">
                        {d?.delivery_order?.shipment?.shipper?.name}
                      </div>
                    }
                  />

                  {d?.delivery_order?.shipment?.cod_value > 0 && (
                    <ListHorizontal
                      title="COD"
                      content={
                        <div className="fw-bold">
                          {currencyFormat(
                            d?.delivery_order?.shipment?.cod_value,
                            "Rp"
                          )}
                        </div>
                      }
                    />
                  )}

                  <ListHorizontal
                    title="ETA"
                    content={
                      <div className="fw-bold">
                        {formatDate(
                          d?.delivery_order?.shipment?.eta_at,
                          "DD MMM YYYY, HH:mm"
                        )}
                      </div>
                    }
                  />

                  {d?.delivery_order?.shipment?.items && (
                    <>
                      <div className="mt-3 badge status-console-lt ms-auto">
                        {" "}
                        List Item{" "}
                      </div>

                      {d?.delivery_order?.shipment?.items?.map((item, idx) => (
                        <div
                          key={idx}
                          className="row border-bottom py-1 align-items-center"
                        >
                          <div className="col-1 pe-0">{idx + 1}.</div>
                          <div className="col-7 fs-8 fw-semibold ps-0">
                            <div className="table-col">
                              <div className="text-ellipsis fs-8 fw-semibold text-uppercase">
                                {item?.item_name}
                              </div>
                            </div>
                          </div>
                          <div className="col-2 fs-8">
                            <div className="table-col">
                              <div className="text-ellipsis fs-8 fw-semibold text-uppercase">
                                {item?.quantity} Koli
                              </div>
                            </div>
                          </div>
                          <div className="col-2 fs-8">
                            <div className="table-col">
                              <div className="text-ellipsis fs-8 fw-semibold text-uppercase">
                                {item?.weight} KG
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </CardContainerDetail>

              )}


            </>

          ))}
      </div>

      <Modal
        show={show}
        onHide={() => {
          setJobs([]);
          setShow(false);
        }}
        backdrop="static"
        size="small"
        enforceFocus={true}
        dialogClassName="sc-modals"
      >
        <Modal.Header closeButton>
          <div className="fw-bold text-uppercase">Daftar Pickup</div>
        </Modal.Header>
        <Modal.Body className="bg-white">
          {data?.jobs && (
            <div>
              {data?.jobs?.map((d, key) => (
                <label
                  className="row py-2"
                  key={key}
                  style={{
                    borderBottom: "0.5px solid #202020",
                  }}
                >
                  <div className="col-1 fw-semibold" style={{ fontSize: 11 }}>
                    <input
                      type="checkbox"
                      onChange={() => handleChecklist(d?.id)}
                      checked={jobs.includes(d?.id)}
                      style={{ transform: "scale(1.5)", marginTop: 5 }}
                    />
                  </div>
                  <div className="col-4">
                    <div style={{ fontSize: 11 }} className="fw-semibold">
                      {d?.delivery_order?.shipment?.code}
                    </div>
                    <div style={{ fontSize: 9 }}>
                      ORD: {d?.delivery_order?.order?.code}
                    </div>

                    {d?.delivery_order?.shipment?.ref_code && (
                      <div style={{ fontSize: 9 }}>
                        REF: {d?.delivery_order?.shipment?.ref_code}
                      </div>
                    )}
                  </div>
                  <div className="col-5">
                    <div style={{ fontSize: 11 }} className="fw-semibold">
                      {d?.destination?.name}
                    </div>
                    <div style={{ fontSize: 9 }}>
                      {d?.destination?.region?.district_name},{" "}
                      {d?.destination?.region?.regency_name},{" "}
                      {d?.destination?.region?.province_name}
                    </div>
                  </div>
                  <div className="col-2">
                    <div style={{ fontSize: 11 }} className="fw-semibold">
                      {currencyFormat(
                        d?.delivery_order?.shipment?.total_weight
                      )}{" "}
                      KG
                    </div>
                    <div style={{ fontSize: 9 }}>
                      {d?.delivery_order?.shipment?.total_quantity} Koli
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-white">
          <button className="btn btn-success btn-sm" onClick={nextStep}>
            Selanjutnya
          </button>
        </Modal.Footer>
      </Modal>
    </Page>
  );
};

export default LegScreen;
